// PR-16.1 — pure error-message mapper for League CRUD mutations. Kept
// in lib/ so vitest can import without the supabase client.
//
// Background: when LeagueForm's saveMutation hits the new
// leagues_organization_name_unique_idx UNIQUE constraint (PR-16.1
// migration), Postgres returns a PostgrestError with code "23505"
// (unique_violation). The default `err.message` reads:
//
//   "duplicate key value violates unique constraint
//    \"leagues_organization_name_unique_idx\""
//
// — accurate but not user-friendly. This helper maps known error shapes
// to clean Swedish strings. Falls back to a generic message + the raw
// `err.message` for unknown errors so admins still get useful detail.

/** Shape we accept — covers PostgrestError, Error, and plain { message, code }. */
interface ErrorLike {
  message?: unknown;
  code?: unknown;
  // Some clients put the SQL state in `details` or in a nested `cause`;
  // we only check the top-level `code` field for v1.
}

/**
 * Maps a save-time error from the LeagueForm saveMutation to a
 * user-facing Swedish string suitable for `toast.error(...)`.
 *
 * Returns `null` if `err` doesn't look like an Error (caller should
 * fall back to a generic "Kunde inte spara serie" string).
 *
 * Handled codes (extensible):
 *   - 23505 (unique_violation):
 *     - if the constraint name contains "leagues_organization_name" →
 *       "En serie med detta namn finns redan i organisationen."
 *     - otherwise → generic "Värdet finns redan: $message"
 */
export function parseLeagueSaveError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const e = err as ErrorLike;
  const message = typeof e.message === "string" ? e.message : "";
  const code = typeof e.code === "string" ? e.code : "";

  // 23505 — UNIQUE-constraint violation
  if (code === "23505" || message.includes("duplicate key value")) {
    if (message.includes("leagues_organization_name")) {
      return "En serie med detta namn finns redan i organisationen.";
    }
    // Generic UNIQUE violation — surface the message for diagnostic
    return message ? `Värdet finns redan: ${message}` : "Värdet finns redan.";
  }

  // 23514 — CHECK-constraint violation (e.g. min/max player range)
  if (code === "23514") {
    return message
      ? `Validering misslyckades: ${message}`
      : "Validering misslyckades på databasnivå.";
  }

  // 23502 — NOT NULL violation
  if (code === "23502") {
    return message
      ? `Obligatoriskt fält saknas: ${message}`
      : "Obligatoriskt fält saknas.";
  }

  // Unknown — return null so caller falls back to generic toast
  return null;
}
